import { formatReadableNumber } from '@lfg/core/lib';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, IconButton, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { useState } from 'react';

import { DataTable } from 'src/components/DataTable';
import { AppPagination } from 'src/components/Pagination';
import { colors } from 'src/global/styles/colors';
import { useProjectDashboardContext } from 'src/hooks/useProjectDashboardContext';
import { useGetProjectFundingDetailsQuery } from 'src/store/services/project-dashboard.service';

// No mock data needed - using real API data

const FundingDistributionTable = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [expandedSales, setExpandedSales] = useState<Set<string>>(new Set());
  const { projectId, address, network } = useProjectDashboardContext();

  const {
    data: fundingDetailsResponse,
    isLoading,
    error,
  } = useGetProjectFundingDetailsQuery(
    {
      projectId,
      page,
      size: limit,
      address,
      network,
    },
    { skip: !projectId || !address || !network },
  );

  const fundingDetails = fundingDetailsResponse?.data || [];
  const meta = fundingDetailsResponse?.meta;

  const toggleSale = (saleAddress: string) => {
    setExpandedSales(prev => {
      const newSet = new Set(prev);
      if (newSet.has(saleAddress)) {
        newSet.delete(saleAddress);
      } else {
        newSet.add(saleAddress);
      }
      return newSet;
    });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage + 1);
  };

  const totalRevenue = fundingDetails.reduce((sum, detail) => sum + detail.revenues, 0);
  const totalNodesSold = fundingDetails.reduce((sum, detail) => sum + detail.totalNode, 0);

  // Group funding details by sale address
  const groupedBySale = fundingDetails.reduce(
    (acc, detail) => {
      if (!acc[detail.sale]) {
        acc[detail.sale] = [];
      }
      acc[detail.sale].push(detail);
      return acc;
    },
    {} as Record<string, typeof fundingDetails>,
  );

  return (
    <Box
      sx={{
        background: colors.neutral03,
        backdropFilter: 'blur(100px)',
        borderRadius: '8px',
        padding: '24px',
      }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography
            sx={{
              color: colors.white,
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '24px',
            }}>
            Funding Distribution Details
          </Typography>

          <Button
            startIcon={<DownloadIcon />}
            sx={{
              backgroundColor: colors.neutral06,
              color: colors.white,
              fontSize: '14px',
              fontWeight: 500,
              borderRadius: '4px',
              padding: '10px 24px',
              '&:hover': {
                backgroundColor: colors.neutral015,
              },
            }}>
            Download Data
          </Button>
        </Stack>

        {/* Table */}
        <DataTable
          isEmpty={!fundingDetails.length && !isLoading}
          skeletonRowCount={5}
          isLoading={isLoading}
          renderHeader={() => (
            <TableRow
              sx={{
                backgroundColor: colors.neutral06,
              }}>
              <TableCell sx={{ paddingLeft: '16px' }}>Sale Address</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Revenue (USD)</TableCell>
              <TableCell>No. of Nodes</TableCell>
              <TableCell>Updated At</TableCell>
            </TableRow>
          )}
          renderBody={() => (
            <>
              {error && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ color: colors.red200 }}>Failed to load funding details data</Typography>
                  </TableCell>
                </TableRow>
              )}

              {!error && fundingDetails.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ color: colors.neutral90 }}>No funding details available</Typography>
                  </TableCell>
                </TableRow>
              )}

              {/* Summary Row */}
              {fundingDetails.length > 0 && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Summary Total</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {formatReadableNumber(totalRevenue, { maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{totalNodesSold.toLocaleString()}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}

              {/* Sale Rows */}
              {Object.entries(groupedBySale).map(([saleAddress, details]) => (
                <>
                  {/* Sale Header */}
                  <TableRow key={saleAddress}>
                    <TableCell colSpan={5} sx={{ backgroundColor: colors.neutral03 }}>
                      <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton size='small' onClick={() => toggleSale(saleAddress)} sx={{ color: colors.white }}>
                          {expandedSales.has(saleAddress) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Typography sx={{ color: colors.white, fontSize: '14px', fontWeight: 500 }}>
                          {saleAddress.slice(0, 6)}...{saleAddress.slice(-4)}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>

                  {/* Sale Details */}
                  {details.map((detail, detailIndex) => (
                    <TableRow
                      key={`${saleAddress}-${detail.position}-${detailIndex}`}
                      sx={{
                        display: expandedSales.has(saleAddress) ? 'table-row' : 'none',
                      }}>
                      <TableCell sx={{ pl: 4 }}>Position {detail.position}</TableCell>
                      <TableCell>{detail.position}</TableCell>
                      <TableCell>{formatReadableNumber(detail.revenues, { maximumFractionDigits: 2 })}</TableCell>
                      <TableCell>{detail.totalNode?.toLocaleString()}</TableCell>
                      <TableCell>{new Date(detail.updatedAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </>
              ))}
            </>
          )}
        />

        {/* Pagination */}
        {meta && meta.total > 0 && (
          <Stack direction={'row'} justifyContent={'end'} marginTop={2}>
            <AppPagination
              sx={{
                border: 'none',
              }}
              count={meta.total}
              page={page - 1}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default FundingDistributionTable;
